import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Compass, Calendar, Users, Settings, Map, ChevronLeft, ChevronRight, Clock, 
  ChevronUp, ChevronDown, Plus, Info, BookText, GripVertical, Trash2, Globe, 
  MapPin, Copy, Layers, Briefcase, FileText, Search, Layout, User, FileQuestion, 
  Bookmark, BookmarkPlus, EyeOff, MoreHorizontal 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { QuotationPage } from '@/components/quotation/types';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from '@/components/ui/input';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Separator } from '@/components/ui/separator';
import { useLibrary } from '@/hooks/useLibrary';
import { LibraryBrowser } from '@/components/quotation/LibraryBrowser';
import { SaveToLibraryModal } from '@/components/quotation/SaveToLibraryModal';
import { PageActionsMenu } from './PageActionsMenu';

interface SidebarLinkProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  onClick?: () => void;
  safeNavigate?: (path: string) => void;
}
function SidebarLink({ to, icon, label, active, onClick, safeNavigate }: SidebarLinkProps) {
  // Função handle click que utiliza safeNavigate se disponível
  const handleClick = (e: React.MouseEvent) => {
    if (safeNavigate) {
      e.preventDefault(); // Prevenir navegação padrão
      safeNavigate(to);
    }
    
    if (onClick) {
      onClick();
    }
  };
  return (
    <Link
      to={to}
      className={cn(
        "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
        active 
          ? "bg-voah-coral text-white" 
          : "text-white/90 hover:text-white hover:bg-white/10"
      )}
      onClick={handleClick}
    >
      {React.cloneElement(icon as React.ReactElement, {
        className: "h-5 w-5",
      })}
      <span>{label}</span>
    </Link>
  );
}
interface QuotationPagesSectionProps {
  pages: QuotationPage[];
  activePage: string | null;
  setActivePage: (pageId: string | null) => void;
  onAddPage?: () => void;
  reorderPages?: (sourceIndex: number, destinationIndex: number) => void;
  removePage?: (pageId: string) => void;
  duplicatePage?: (pageId: string) => void;
  togglePageVisibility?: (pageId: string) => void;
  useDayMode?: boolean;
  copyPageToQuotation?: (pageId: string, targetQuotationId: string) => void;
  currentQuotationId?: string;
}
function QuotationPagesSection({
  pages,
  activePage,
  setActivePage,
  onAddPage,
  reorderPages,
  removePage,
  duplicatePage,
  togglePageVisibility,
  useDayMode,
  copyPageToQuotation,
  currentQuotationId
}: QuotationPagesSectionProps) {
  const location = useLocation();
  const isQuotationEditRoute = location.pathname.includes('/admin/quotations/');
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [menuTriggerRect, setMenuTriggerRect] = useState<DOMRect | null>(null);
  
  // Retornar null se não estivermos em uma rota de edição de cotação
  if (!isQuotationEditRoute) {
    return null;
  }
  
  const handleMenuToggle = (pageId: string, event: React.MouseEvent) => {
    // Salvar o rect do botão que foi clicado
    if (event.currentTarget) {
      setMenuTriggerRect(event.currentTarget.getBoundingClientRect());
    }
    
    setActiveMenu(activeMenu === pageId ? null : pageId);
  };
  
  const handleCloseMenu = () => {
    setActiveMenu(null);
    setMenuTriggerRect(null);
  };

  return (
    <div className="px-3 pt-2">
      <Accordion type="single" collapsible defaultValue="pages" className="w-full">
        <AccordionItem value="pages" className="border-none">
          <AccordionTrigger className="py-2 px-2 hover:no-underline text-white/90 hover:text-white hover:bg-white/10">
            <div className="flex items-center gap-2">
              <Layout className="h-5 w-5" />
              <span className="text-sm font-medium">Páginas</span>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-1 mt-1">
              {/* Add page button */}
              {onAddPage && (
                <button
                  onClick={onAddPage}
                  className="w-full flex items-center gap-2 px-2 py-2 text-sm font-medium rounded-md text-white/90 hover:text-white hover:bg-white/10"
                >
                  <Plus className="h-4 w-4" />
                  <span>Adicionar página</span>
                </button>
              )}
              
              {/* Pages list */}
              {pages.length > 0 && (
                <DragDropContext
                  onDragEnd={(result: DropResult) => {
                    if (!result.destination || !reorderPages) return;
                    reorderPages(result.source.index, result.destination.index);
                  }}
                >
                  <Droppable droppableId="pages">
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className="space-y-1"
                      >
                        {pages.map((page, index) => (
                          <Draggable
                            key={page.id}
                            draggableId={page.id}
                            index={index}
                          >
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className="group relative"
                              >
                                <div
                                  className={cn(
                                    "w-full flex items-center gap-2 px-2 py-2 text-sm font-medium rounded-md cursor-pointer",
                                    activePage === page.id
                                      ? "bg-voah-coral text-white"
                                      : "text-white/90 hover:text-white hover:bg-white/10",
                                    page.isVisible === false && "opacity-50"
                                  )}
                                  onClick={() => setActivePage(page.id)}
                                >
                                  <div {...provided.dragHandleProps}>
                                    <GripVertical className="h-4 w-4 cursor-grab active:cursor-grabbing" />
                                  </div>
                                  <span className={cn(page.isVisible === false && "line-through")}>
                                    {useDayMode 
                                      ? (page.dayTitle || page.title || `Dia ${index + 1}`)
                                      : (page.pageTitle || page.title || `Página ${index + 1}`)}
                                  </span>
                                  {page.isVisible === false && (
                                    <EyeOff className="h-3 w-3 text-white/70 ml-1" />
                                  )}
                                </div>
                                
                                <div className="absolute right-2 top-1/2 -translate-y-1/2 hidden group-hover:flex items-center gap-1 z-10">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 text-white/70 hover:text-white hover:bg-white/10"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMenuToggle(page.id, e);
                                    }}
                                  >
                                    <MoreHorizontal className="h-3 w-3" />
                                  </Button>
                                </div>
                                
                                <div className="relative">
                                  {activeMenu === page.id && (
                                    <PageActionsMenu
                                      pageId={page.id}
                                      pageTitle={useDayMode 
                                        ? (page.dayTitle || page.title || `Dia ${index + 1}`)
                                        : (page.pageTitle || page.title || `Página ${index + 1}`)}
                                      isVisible={page.isVisible !== false}
                                      isOpen={activeMenu === page.id}
                                      onClose={handleCloseMenu}
                                      onToggleVisibility={togglePageVisibility || (() => {})}
                                      onDuplicate={duplicatePage || (() => {})}
                                      onDelete={removePage || (() => {})}
                                      canDelete={!!removePage && pages.length > 1}
                                      onCopyToQuotation={copyPageToQuotation}
                                      currentQuotationId={currentQuotationId}
                                      triggerRect={menuTriggerRect}
                                    />
                                  )}
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
interface SidebarProps {
  quotationPages?: QuotationPage[];
  activePage?: string | null;
  setActivePage?: (pageId: string | null) => void;
  addPage?: () => void;
  removePage?: (pageId: string) => void;
  reorderPages?: (sourceIndex: number, destinationIndex: number) => void;
  duplicatePage?: (pageId: string) => void;
  copyPageToQuotation?: (pageId: string, targetQuotationId: string) => void;
  togglePageVisibility?: (pageId: string) => void;
  onSelectQuotationBasicInfo?: () => void;
  useDayMode?: boolean;
  safeNavigate?: (path: string) => void;
  currentQuotationId?: string;
}
export function Sidebar({
  quotationPages = [],
  activePage = null,
  setActivePage = () => {},
  addPage,
  removePage,
  reorderPages,
  duplicatePage,
  togglePageVisibility,
  onSelectQuotationBasicInfo,
  useDayMode,
  safeNavigate,
  copyPageToQuotation,
  currentQuotationId
}: SidebarProps) {
  const location = useLocation();
  const isQuotationRoute = location.pathname.includes('/admin/quotations');
  
  // State for the SaveToLibraryModal
  const [isSaveToLibraryOpen, setIsSaveToLibraryOpen] = useState(false);
  // Library hook for the browser functionality
  const { 
    isBrowserOpen, 
    selectedItemType,
    openLibraryBrowser, 
    closeLibraryBrowser, 
    addLibraryItemToQuotation,
    refreshLibrary
  } = useLibrary();
  // Handler for adding library items to quotation
  const handleSelectLibraryItem = (item: any) => {
    addLibraryItemToQuotation(item);
  };
  // Handler for saving an item to the library
  const handleSaveToLibrary = (libraryItem: any) => {
    // Close the modal
    setIsSaveToLibraryOpen(false);
    
    // Refresh the library items to include the new item
    refreshLibrary();
  };
  // Open the library browser modal
  const handleOpenLibraryBrowser = () => {
    openLibraryBrowser();
  };
  // Open the save to library modal
  const handleOpenSaveToLibrary = () => {
    setIsSaveToLibraryOpen(true);
  };
  return (
    <div className="w-64 h-screen bg-voah-teal">
      <div className="flex flex-col h-full">
        <div className="flex items-center p-3 border-b border-white/10">
          <div className="text-white font-semibold text-lg">VOAH Admin</div>
        </div>
      
        <div className="flex-1 overflow-y-auto py-3 px-1.5">
          <div className="space-y-1 mb-6">
            <SidebarLink
              to="/admin"
              icon={<Compass />}
              label="Dashboard"
              active={location.pathname === '/admin'}
              safeNavigate={safeNavigate}
            />
            <SidebarLink
              to="/admin/travel-hub"
              icon={<Globe />}
              label="Travel Hub"
              active={location.pathname === '/admin/travel-hub'}
              safeNavigate={safeNavigate}
            />
            <SidebarLink
              to="/admin/places-search"
              icon={<Search />}
              label="Busca de Lugares"
              active={location.pathname === '/admin/places-search'}
              safeNavigate={safeNavigate}
            />
            <SidebarLink
              to="/admin/clients"
              icon={<Users />}
              label="Clientes"
              active={location.pathname === '/admin/clients'}
              safeNavigate={safeNavigate}
            />
            
            {/* Biblioteca buttons */}
            <div className="pt-2 border-t border-white/10 mt-2">
              {/* Browse library button */}
              <div 
                className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer text-white/90 hover:text-white hover:bg-white/10"
                onClick={handleOpenLibraryBrowser}
              >
                <Bookmark className="h-5 w-5" />
                <span>Biblioteca</span>
              </div>
              
              {/* Create library item button */}
              <div 
                className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer text-white/90 hover:text-white hover:bg-white/10"
                onClick={handleOpenSaveToLibrary}
              >
                <BookmarkPlus className="h-5 w-5" />
                <span>Criar item</span>
              </div>
            </div>
          </div>
          
          {/* Informações básicas para Cotação */}
          {isQuotationRoute && onSelectQuotationBasicInfo && location.pathname.includes('/admin/quotations/') && (
            <>
              <Separator className="my-2 bg-white/10" />
              <div className="px-3 py-2">
                <button
                  className={cn(
                    "w-full flex items-center gap-2 px-2 py-2 text-sm font-medium rounded-md",
                    activePage === null 
                      ? "bg-voah-coral text-white" 
                      : "text-white/90 hover:text-white hover:bg-white/10"
                  )}
                  onClick={onSelectQuotationBasicInfo}
                >
                  <Info className="h-5 w-5" />
                  <span>Informações Gerais</span>
                </button>
              </div>
            </>
          )}
          
          <QuotationPagesSection
            pages={quotationPages}
            activePage={activePage}
            setActivePage={setActivePage}
            onAddPage={addPage}
            reorderPages={reorderPages}
            removePage={removePage}
            duplicatePage={duplicatePage}
            togglePageVisibility={togglePageVisibility}
            useDayMode={useDayMode}
            copyPageToQuotation={copyPageToQuotation}
            currentQuotationId={currentQuotationId}
          />
        </div>
      </div>
      
      {/* Library Browser Modal */}
      <LibraryBrowser
        isOpen={isBrowserOpen}
        onClose={closeLibraryBrowser}
        itemType={selectedItemType}
        onSelectItem={handleSelectLibraryItem}
      />
      
      {/* Save to Library Modal */}
      <SaveToLibraryModal
        isOpen={isSaveToLibraryOpen}
        onClose={() => setIsSaveToLibraryOpen(false)}
        onSave={handleSaveToLibrary}
      />
    </div>
  );
}